/**
 * Job Search API Service
 * Integrates with JSearch API (RapidAPI) to fetch real job listings
 * from LinkedIn, Indeed, Glassdoor, ZipRecruiter, and more
 */

const JSEARCH_API_KEY = '92b688277dmshafa9b6b74f2760ap15c98djsn6812203e9dda'; // User needs to add their key
const JSEARCH_API_HOST = 'jsearch.p.rapidapi.com';
const JSEARCH_BASE_URL = 'https://jsearch.p.rapidapi.com';

/**
 * Search for ALL types of jobs (Full-Time, Part-Time, Contract, Internship)
 * 
 * @param {Object} params - Search parameters
 * @param {string} params.query - Search query (e.g., "software developer")
 * @param {string} params.location - Location filter (e.g., "United Kingdom")
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.num_pages - Number of pages (default: 1)
 * @returns {Promise<Object>} Job search results
 */
export async function searchJSearch({
  query = 'developer',
  location = 'United Kingdom',
  page = 1,
  num_pages = 1,
  remote_jobs_only = false,
}) {
  try {
    // Build query string with location
    const searchQuery = location 
      ? `${query} in ${location}`
      : query;

    // Build URL with parameters
    const params = new URLSearchParams({
      query: searchQuery,
      page: page.toString(),
      num_pages: num_pages.toString(),
      date_posted: 'week', // Jobs from last week
      // employment_types removed to search ALL job types (Full-Time, Part-Time, Contract, Internship)
      remote_jobs_only: remote_jobs_only.toString(),
    });

    const url = `${JSEARCH_BASE_URL}/search?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-RapidAPI-Key': JSEARCH_API_KEY,
        'X-RapidAPI-Host': JSEARCH_API_HOST,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Transform API response to match our app's data structure
    return {
      success: true,
      jobs: transformJSearchJobs(data.data || []),
      total: data.total || 0,
      page: data.page || 1,
      raw: data, // Keep raw data for debugging
    };
  } catch (error) {
    console.error('JSearch API Error:', error);
    return {
      success: false,
      error: error.message,
      jobs: [],
      total: 0,
    };
  }
}

/**
 * Transform JSearch API response to our app's job format
 * @param {Array} apiJobs - Jobs from JSearch API
 * @returns {Array} Transformed jobs
 */
function transformJSearchJobs(apiJobs) {
  return apiJobs.map((job, index) => {
    // Extract salary information
    const salaryInfo = extractSalaryInfo(job);
    
    // Generate match score based on job attributes
    const matchScore = calculateMatchScore(job);

    return {
      id: job.job_id || `api_${Date.now()}_${index}`,
      jobId: job.job_id || `api_${Date.now()}_${index}`,
      title: job.job_title || 'Untitled Position',
      client: job.employer_name || 'Company',
      company: job.employer_name || 'Company',
      clientLogo: job.employer_logo || generateLogoPlaceholder(job.employer_name),
      location: job.job_city && job.job_country 
        ? `${job.job_city}, ${job.job_country}`
        : job.job_country || 'Remote',
      budget: salaryInfo.budget,
      budgetRange: salaryInfo.budgetRange,
      salary: salaryInfo.budget,
      type: transformJobType(job.job_employment_type),
      jobType: transformJobType(job.job_employment_type),
      projectType: job.job_employment_type || 'Contract',
      duration: estimateDuration(job),
      remote: job.job_is_remote || false,
      isRemote: job.job_is_remote || false,
      posted: formatPostedDate(job.job_posted_at_datetime_utc),
      description: job.job_description || 'No description available',
      requirements: extractRequirements(job),
      skills: extractSkills(job),
      requiredSkills: extractSkills(job),
      deliverables: extractDeliverables(job),
      matchScore: matchScore,
      featured: job.job_highlighted || false,
      urgent: isUrgent(job),
      
      // Client/Employer info
      clientInfo: {
        rating: 4.5, // Default rating (API doesn't provide this)
        reviewsCount: 0,
        projectsPosted: 0,
        hireRate: 'N/A',
        location: job.job_city || job.job_country || 'Unknown',
        memberSince: 'N/A',
      },
      
      // Payment terms
      paymentTerms: {
        type: salaryInfo.paymentType,
        milestones: false,
        escrow: false,
        paymentVerified: job.employer_name ? true : false,
      },
      
      // Additional API data
      apiSource: 'JSearch',
      applyLink: job.job_apply_link,
      jobPublisher: job.job_publisher,
      jobGoogleLink: job.job_google_link,
      
      // Original API data for reference
      _originalData: job,
    };
  });
}

/**
 * Extract salary information from job data
 */
function extractSalaryInfo(job) {
  const min = job.job_min_salary;
  const max = job.job_max_salary;
  const currency = job.job_salary_currency || 'GBP';
  const period = job.job_salary_period || 'YEAR';

  let budget = 'Competitive';
  let budgetRange = { min: 0, max: 0 };
  let paymentType = 'Fixed Price';

  if (min && max) {
    // Convert to appropriate format
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency;
    
    if (period === 'YEAR') {
      budget = `${symbol}${formatNumber(min)} - ${symbol}${formatNumber(max)}/year`;
      paymentType = 'Annual Salary';
    } else if (period === 'MONTH') {
      budget = `${symbol}${formatNumber(min)} - ${symbol}${formatNumber(max)}/month`;
      paymentType = 'Monthly Rate';
    } else if (period === 'HOUR') {
      budget = `${symbol}${min} - ${symbol}${max}/hour`;
      paymentType = 'Hourly Rate';
    } else {
      budget = `${symbol}${formatNumber(min)} - ${symbol}${formatNumber(max)}`;
    }
    
    budgetRange = { min, max };
  } else if (min) {
    const symbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency;
    budget = `From ${symbol}${formatNumber(min)}`;
    budgetRange = { min, max: min * 1.5 };
  }

  return { budget, budgetRange, paymentType };
}

/**
 * Format large numbers with K suffix
 */
function formatNumber(num) {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`;
  }
  return num.toString();
}

/**
 * Transform job employment type
 */
function transformJobType(employmentType) {
  const typeMap = {
    'CONTRACTOR': 'Freelance',
    'PARTTIME': 'Part-time',
    'FULLTIME': 'Full-time',
    'INTERN': 'Internship',
  };
  return typeMap[employmentType] || 'Contract';
}

/**
 * Estimate project duration based on job type
 */
function estimateDuration(job) {
  const type = job.job_employment_type;
  
  if (type === 'CONTRACTOR') {
    return '1-3 months';
  } else if (type === 'PARTTIME') {
    return '2-6 months';
  }
  
  return 'Flexible';
}

/**
 * Format posted date to relative time
 */
function formatPostedDate(dateString) {
  if (!dateString) return 'Recently';
  
  const posted = new Date(dateString);
  const now = new Date();
  const diffMs = now - posted;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  
  return 'Recently';
}

/**
 * Extract requirements from job description
 */
function extractRequirements(job) {
  const requirements = [];
  
  // Add job qualifications if available
  if (job.job_required_experience) {
    const exp = job.job_required_experience;
    if (exp.required_experience_in_months) {
      const years = Math.floor(exp.required_experience_in_months / 12);
      if (years > 0) {
        requirements.push(`${years}+ years experience`);
      }
    }
  }
  
  // Add education if specified
  if (job.job_required_education) {
    const edu = job.job_required_education;
    if (edu.degree_mentioned) {
      requirements.push(edu.degree_mentioned);
    }
  }
  
  // Extract from description (simple keyword matching)
  const description = job.job_description || '';
  const keywords = [
    'Bachelor', 'Master', 'PhD', 'Degree',
    'experience', 'years',
    'certification', 'certified',
  ];
  
  // If no requirements found, add generic ones
  if (requirements.length === 0) {
    requirements.push('Relevant experience');
    requirements.push('Strong communication skills');
    requirements.push('Self-motivated');
  }
  
  return requirements.slice(0, 5); // Limit to 5 requirements
}

/**
 * Extract skills from job data
 */
function extractSkills(job) {
  const skills = [];
  
  // Add required skills if available
  if (job.job_required_skills && Array.isArray(job.job_required_skills)) {
    skills.push(...job.job_required_skills);
  }
  
  // Extract from job highlights
  if (job.job_highlights) {
    if (job.job_highlights.Qualifications) {
      // Parse qualifications for skills
      job.job_highlights.Qualifications.forEach(qual => {
        // Simple extraction - look for technology keywords
        const techKeywords = qual.match(/\b(React|Node|Python|Java|JavaScript|TypeScript|AWS|Docker|SQL|MongoDB|Angular|Vue|PHP|Ruby|Go|Swift|Kotlin|Flutter|Django|Laravel|Spring|\.NET)\b/gi);
        if (techKeywords) {
          skills.push(...techKeywords);
        }
      });
    }
  }
  
  // If no skills found, extract from title
  if (skills.length === 0) {
    const title = job.job_title || '';
    const commonSkills = ['Communication', 'Problem Solving', 'Teamwork', 'Time Management'];
    skills.push(...commonSkills);
  }
  
  // Remove duplicates and limit
  return [...new Set(skills)].slice(0, 6);
}

/**
 * Extract deliverables from job description
 */
function extractDeliverables(job) {
  const deliverables = [
    'Project Completion',
    'Documentation',
    'Quality Assurance',
  ];
  
  // Add specific deliverables based on job type
  const title = (job.job_title || '').toLowerCase();
  
  if (title.includes('developer') || title.includes('engineer')) {
    deliverables.push('Source Code', 'Testing');
  } else if (title.includes('design')) {
    deliverables.push('Design Files', 'Revisions');
  } else if (title.includes('writer') || title.includes('content')) {
    deliverables.push('Written Content', 'Revisions');
  }
  
  return deliverables.slice(0, 5);
}

/**
 * Calculate match score based on job attributes
 */
function calculateMatchScore(job) {
  let score = 60; // Base score
  
  // Boost for remote jobs
  if (job.job_is_remote) score += 10;
  
  // Boost for recent postings
  const posted = new Date(job.job_posted_at_datetime_utc);
  const daysSincePosted = (new Date() - posted) / (1000 * 60 * 60 * 24);
  if (daysSincePosted < 3) score += 15;
  else if (daysSincePosted < 7) score += 10;
  
  // Boost for highlighted jobs
  if (job.job_highlighted) score += 5;
  
  // Boost for jobs with good salary info
  if (job.job_min_salary && job.job_max_salary) score += 10;
  
  // Random variation
  score += Math.floor(Math.random() * 10);
  
  return Math.min(score, 99); // Cap at 99
}

/**
 * Check if job is urgent
 */
function isUrgent(job) {
  const posted = new Date(job.job_posted_at_datetime_utc);
  const daysSincePosted = (new Date() - posted) / (1000 * 60 * 60 * 24);
  
  // Consider urgent if posted within last 2 days
  return daysSincePosted < 2;
}

/**
 * Generate placeholder logo based on company name
 */
function generateLogoPlaceholder(companyName) {
  if (!companyName) return null;
  
  // Try Clearbit logo API first
  const domain = companyName.toLowerCase().replace(/\s+/g, '') + '.com';
  return `https://logo.clearbit.com/${domain}`;
}

/**
 * Get job details by ID
 */
export async function getJobDetails(jobId) {
  try {
    const url = `${JSEARCH_BASE_URL}/job-details?job_id=${jobId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'X-RapidAPI-Key': JSEARCH_API_KEY,
        'X-RapidAPI-Host': JSEARCH_API_HOST,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    const data = await response.json();
    return {
      success: true,
      job: transformJSearchJobs(data.data || [])[0],
    };
  } catch (error) {
    console.error('Job Details API Error:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Check if API key is configured
 */
export function isAPIConfigured() {
  return JSEARCH_API_KEY && JSEARCH_API_KEY !== 'YOUR_RAPIDAPI_KEY_HERE';
}

// Keep old name for backward compatibility
export const searchFreelanceJobs = searchJSearch;

export default {
  searchJSearch,
  searchFreelanceJobs, // Backward compatibility
  getJobDetails,
  isAPIConfigured,
};